import { useState, useEffect } from "react";
import { useAdminAuth } from "../components/AdminLayout";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Building2,
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Trash2,
  ExternalLink,
} from "lucide-react";

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  trialing: "bg-blue-50 text-blue-700 border-blue-200",
  incomplete: "bg-amber-50 text-amber-700 border-amber-200",
  past_due: "bg-red-50 text-red-700 border-red-200",
  canceled: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${STATUS_STYLES[status] || STATUS_STYLES.incomplete}`}
    >
      {status}
    </span>
  );
}

export default function AdminOrgs() {
  const { admin } = useAdminAuth();
  const [orgs, setOrgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [creatingDemo, setCreatingDemo] = useState(false);
  const [showDemoConfirm, setShowDemoConfirm] = useState(false);
  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    loadOrgs();
  }, []);

  const loadOrgs = async () => {
    try {
      const res = await axios.get(`${API}/admin/orgs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrgs(res.data.organizations || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  const loadOrgDetail = async (orgId) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
      return;
    }
    setExpandedOrg(orgId);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orgs/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrgDetail(res.data);
    } catch {
      toast.error("Failed to load org details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleActivate = async (orgId) => {
    try {
      await axios.post(
        `${API}/admin/orgs/${orgId}/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Organization activated");
      loadOrgs();
      if (expandedOrg === orgId) loadOrgDetail(orgId);
    } catch {
      toast.error("Failed to activate organization");
    }
  };

  const handleStatusChange = async (orgId, status) => {
    try {
      await axios.post(
        `${API}/admin/orgs/${orgId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status set to ${status}`);
      loadOrgs();
      if (expandedOrg === orgId) loadOrgDetail(orgId);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleCreateDemoOrg = async () => {
    setShowDemoConfirm(false);
    setCreatingDemo(true);
    try {
      const res = await axios.post(
        `${API}/admin/orgs/create-demo`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const org = res.data.organization;
      toast.success(
        <div>
          <p className="font-medium">Demo org created: {org.name}</p>
          <div className="flex gap-3 mt-2">
            <a
              href={`/admin/orgs`}
              className="text-xs underline"
              data-testid="demo-toast-admin-link"
            >
              View in Admin
            </a>
          </div>
        </div>,
        { duration: 8000 }
      );
      loadOrgs();
    } catch {
      toast.error("Failed to create demo organization");
    } finally {
      setCreatingDemo(false);
    }
  };

  const handleDeleteDemoOrg = async (orgId, orgName) => {
    if (!window.confirm(`Delete demo org "${orgName}" and all its data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/orgs/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Deleted demo org: ${orgName}`);
      if (expandedOrg === orgId) {
        setExpandedOrg(null);
        setOrgDetail(null);
      }
      loadOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete organization");
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="h-40 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div data-testid="admin-orgs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Organizations</h1>
          <p className="text-sm text-muted-foreground">
            {total} organization{total !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="relative">
          <Button
            onClick={() => setShowDemoConfirm(true)}
            disabled={creatingDemo}
            variant="outline"
            size="sm"
            data-testid="create-demo-org-btn"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            {creatingDemo ? "Creating..." : "Create Demo Org"}
          </Button>
          {showDemoConfirm && (
            <div className="absolute right-0 top-full mt-2 w-72 bg-card border border-border rounded-lg shadow-lg p-4 z-50" data-testid="demo-confirm-modal">
              <p className="text-sm font-medium mb-1">Create Demo Organization?</p>
              <p className="text-xs text-muted-foreground mb-3">
                This will seed a realistic org with 3 members, 3 pending invites, and usage data for demos.
              </p>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => setShowDemoConfirm(false)} data-testid="demo-cancel-btn">
                  Cancel
                </Button>
                <Button size="sm" onClick={handleCreateDemoOrg} data-testid="demo-confirm-btn">
                  Create
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {orgs.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <p>No organizations yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orgs.map((org) => (
            <div
              key={org.id}
              className="bg-card border border-border rounded-lg overflow-hidden"
              data-testid={`admin-org-row-${org.id}`}
            >
              {/* Row */}
              <button
                onClick={() => loadOrgDetail(org.id)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/50 transition text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{org.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Created{" "}
                      {new Date(org.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Users className="w-3.5 h-3.5" />
                    {org.member_count}
                  </div>
                  {org.is_demo && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border bg-violet-50 text-violet-700 border-violet-200" data-testid={`demo-badge-${org.id}`}>
                      demo
                    </span>
                  )}
                  <StatusBadge status={org.status} />
                  {expandedOrg === org.id ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </button>

              {/* Expanded detail */}
              {expandedOrg === org.id && (
                <div className="border-t border-border px-5 py-4 bg-muted/30">
                  {detailLoading ? (
                    <div className="animate-pulse h-20" />
                  ) : orgDetail ? (
                    <div className="space-y-4">
                      {/* Actions */}
                      <div className="flex gap-2 flex-wrap">
                        {org.status !== "active" && (
                          <Button
                            size="sm"
                            onClick={() => handleActivate(org.id)}
                            data-testid={`activate-btn-${org.id}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                            Activate
                          </Button>
                        )}
                        {org.status === "active" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              handleStatusChange(org.id, "canceled")
                            }
                          >
                            <XCircle className="w-3.5 h-3.5 mr-1.5" />
                            Cancel
                          </Button>
                        )}
                        {org.status !== "past_due" && org.status !== "active" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              handleStatusChange(org.id, "past_due")
                            }
                          >
                            Past Due
                          </Button>
                        )}
                        {org.is_demo && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleDeleteDemoOrg(org.id, org.name)}
                            data-testid={`delete-demo-btn-${org.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                            Delete Demo
                          </Button>
                        )}
                      </div>

                      {/* Info grid */}
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Plan
                          </p>
                          <p className="font-medium capitalize">
                            {orgDetail.plan_key}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Interval
                          </p>
                          <p className="font-medium capitalize">
                            {orgDetail.interval}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Owner
                          </p>
                          <p className="font-medium text-xs break-all">
                            {orgDetail.owner_user_id?.slice(0, 8)}...
                          </p>
                        </div>
                      </div>

                      {/* Members */}
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                          Members ({orgDetail.members?.length || 0})
                        </p>
                        <div className="space-y-1.5">
                          {orgDetail.members?.map((m) => (
                            <div
                              key={m.id}
                              className="flex items-center justify-between bg-card border border-border rounded-md px-3 py-2 text-sm"
                            >
                              <div>
                                <span className="font-medium">
                                  {m.user_name || m.email}
                                </span>
                                {m.user_name && (
                                  <span className="text-xs text-muted-foreground ml-2">
                                    {m.email}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs px-1.5 py-0.5 bg-muted rounded">
                                  {m.role}
                                </span>
                                {m.invite_status === "pending" && (
                                  <span className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded">
                                    pending
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
